<template>
  <div class="drafts-container">
    <!-- Toolbar -->
    <v-card class="mb-4 drafts-toolbar" flat>
      <v-card-text class="pa-2">
        <v-row align="center" no-gutters>
          <v-col cols="auto" class="ml-2">
            <v-checkbox
              v-model="selectAll"
              hide-details
              density="compact"
              color="primary"
              @change="toggleSelectAll"
            ></v-checkbox>
          </v-col>
          
          <v-col cols="auto">
            <v-menu location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  size="small"
                >
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item @click="selectAll = true; toggleSelectAll()">
                  <v-list-item-title>تحديد الكل</v-list-item-title>
                </v-list-item>
                <v-list-item @click="selectAll = false; toggleSelectAll()">
                  <v-list-item-title>إلغاء تحديد الكل</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          
          <v-col cols="auto">
            <v-btn
              icon
              variant="text"
              size="small"
              color="error"
              :disabled="!hasSelected"
              @click="deleteSelected"
            >
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </v-col>
          
          <v-spacer></v-spacer>
          
          <v-col cols="auto">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="بحث في المسودات"
              single-line
              hide-details
              density="compact"
              variant="outlined"
              class="search-field"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <!-- Drafts List -->
    <v-card flat class="drafts-list">
      <div v-if="loading" class="pa-8 text-center">
        <v-progress-circular indeterminate color="primary" class="mb-4"></v-progress-circular>
        <div class="text-body-1 text-grey">جارِ تحميل المسودات...</div>
      </div>
      
      <v-list v-else lines="two" class="pa-0">
        <v-list-item
          v-for="draft in filteredDrafts"
          :key="draft.id"
          :class="{ 'selected-draft': selectedDrafts.includes(draft.id) }"
          @click="editDraft(draft)"
        >
          <template v-slot:prepend>
            <div class="d-flex align-center">
              <v-checkbox
                v-model="selectedDrafts"
                :value="draft.id"
                hide-details
                density="compact"
                color="primary"
                @click.stop
              ></v-checkbox>
            </div>
          </template>
          
          <v-list-item-title>
            {{ draft.toDepartmentNames?.length ? draft.toDepartmentNames.join('، ') : 'بدون مستلم' }}
            <v-chip
              v-if="draft.ccDepartmentNames?.length"
              size="x-small"
              color="secondary"
              variant="outlined"
              class="mr-2"
            >
              نسخة: {{ draft.ccDepartmentNames.join('، ') }}
            </v-chip>
          </v-list-item-title>
          
          <v-list-item-subtitle class="d-flex">
            <span class="text-truncate">
              {{ draft.subject || 'بدون موضوع' }} - {{ stripHtml(draft.content || '').slice(0, 50) + (stripHtml(draft.content || '').length > 50 ? '...' : '') || 'بدون محتوى' }}
            </span>
          </v-list-item-subtitle>
          
          <template v-slot:append>
            <div class="d-flex flex-column align-end">
              <span class="text-caption">
                {{ formatDate(draft.lastModified) }}
              </span>
              <div class="mt-1 d-flex align-center">
                <v-chip
                  v-if="draft.attachments?.length"
                  size="x-small"
                  color="grey-lighten-3"
                  class="mr-1"
                >
                  <v-icon size="x-small" start>mdi-paperclip</v-icon>
                  {{ draft.attachments.length }}
                </v-chip>
                <v-chip
                  v-if="draft.urgent"
                  size="x-small"
                  color="error"
                  variant="flat"
                  class="mr-1"
                >
                  عاجل
                </v-chip>
              </div>
            </div>
          </template>
        </v-list-item>
        
        <div v-if="!loading && filteredDrafts.length === 0" class="pa-8 text-center">
          <v-icon size="64" color="grey-lighten-1">mdi-file-document-outline</v-icon>
          <div class="text-h6 mt-4 text-grey">لا توجد مسودات</div>
          <div class="text-body-2 text-grey mt-2">يمكنك إنشاء مسودة جديدة من خلال الضغط على "مراسلة جديدة"</div>
        </div>
      </v-list>
    </v-card>
    
    <!-- Edit Draft Dialog -->
    <v-dialog v-model="editDialog" max-width="800">
      <v-card v-if="currentDraft">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>تحرير المسودة</span>
          <v-btn icon variant="text" @click="editDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-divider></v-divider>
        
        <v-card-text class="pt-4">
          <v-form ref="draftForm" @submit.prevent="sendDraft">
            <v-autocomplete
              v-model="currentDraft.to"
              :items="availableDepartments"
              label="إلى الأقسام"
              item-title="name"
              item-value="id"
              variant="outlined"
              class="mb-4"
              multiple
              chips
              closable-chips
            >
              <template v-slot:chip="{ props, item }">
                <v-chip
                  v-bind="props"
                  :text="item.raw.name"
                  class="ma-1"
                  color="primary"
                  variant="outlined"
                ></v-chip>
              </template>
            </v-autocomplete>

            <v-autocomplete
              v-model="currentDraft.cc"
              :items="availableDepartments"
              label="نسخة إلى الأقسام (CC)"
              item-title="name"
              item-value="id"
              variant="outlined"
              class="mb-4"
              multiple
              chips
              closable-chips
              hint="الأقسام التي ستحصل على نسخة من المراسلة"
              persistent-hint
            >
              <template v-slot:chip="{ props, item }">
                <v-chip
                  v-bind="props"
                  :text="item.raw.name"
                  class="ma-1"
                  color="secondary"
                  variant="outlined"
                ></v-chip>
              </template>
            </v-autocomplete>
            
            <v-text-field
              v-model="currentDraft.subject"
              label="الموضوع"
              variant="outlined"
              class="mb-4"
            ></v-text-field>

            <!-- Mail Priority -->
            <v-select
              v-model="currentDraft.mail_priority_id"
              :items="mailPriorities"
              label="أولوية المراسلة"
              item-title="name"
              item-value="id"
              variant="outlined"
              class="mb-4"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <v-icon :color="item.raw.color">mdi-flag</v-icon>
                  </template>
                  <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.raw.desc }}</v-list-item-subtitle>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <v-chip :color="item.raw.color" variant="outlined" size="small">
                  <v-icon start>mdi-flag</v-icon>
                  {{ item.raw.name }}
                </v-chip>
              </template>
            </v-select>
            
            <v-card flat class="mb-2 formatting-toolbar">
              <v-btn-group density="comfortable">
                <v-btn
                  icon
                  variant="text"
                  v-for="(action, index) in textFormatActions"
                  :key="index"
                  :title="action.title"
                  @click="formatText(action.command)"
                  :class="{ 'active-format': action.isActive }"
                >
                  <v-icon>{{ action.icon }}</v-icon>
                </v-btn>
                
                <v-divider vertical class="mx-2"></v-divider>
                
                <v-menu location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      variant="text"
                      v-bind="props"
                      class="font-size-btn"
                    >
                      <v-icon>mdi-format-size</v-icon>
                    </v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item
                      v-for="size in fontSizes"
                      :key="size.value"
                      @click="setFontSize(size.value)"
                    >
                      <v-list-item-title :style="{ fontSize: size.preview }">
                        {{ size.label }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
                
                <v-menu location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      variant="text"
                      v-bind="props"
                    >
                      <v-icon>mdi-palette</v-icon>
                    </v-btn>
                  </template>
                  <v-card min-width="200" class="pa-2">
                    <div class="d-flex flex-wrap">
                      <v-btn
                        v-for="color in textColors"
                        :key="color.value"
                        icon
                        variant="text"
                        :style="{ color: color.value }"
                        @click="setTextColor(color.value)"
                      >
                        <v-icon>mdi-circle</v-icon>
                      </v-btn>
                    </div>
                  </v-card>
                </v-menu>
                
                <v-divider vertical class="mx-2"></v-divider>
                
                <v-btn-group>
                  <v-btn
                    icon
                    variant="text"
                    v-for="align in alignments"
                    :key="align.value"
                    :title="align.title"
                    @click="setAlignment(align.value)"
                  >
                    <v-icon>{{ align.icon }}</v-icon>
                  </v-btn>
                </v-btn-group>
              </v-btn-group>
            </v-card>
            
            <div
              ref="editor"
              class="editor mb-4"
              contenteditable="true"  
              @focusout="updateContent"
              
                         
              v-html="currentDraft.content"
            ></div>
            
            <div class="d-flex align-center mb-4">
              <v-btn
                prepend-icon="mdi-paperclip"
                variant="outlined"
                @click="$refs.fileInput.click()"
              >
                إضافة مرفقات
              </v-btn>
              <input
                ref="fileInput"
                type="file"
                multiple
                class="d-none"
                @change="handleFileUpload"
              >
              <div v-if="currentDraft.attachments?.length" class="ms-4">
                <v-chip
                  v-for="(file, i) in currentDraft.attachments"
                  :key="i"
                  class="me-2"
                  closable
                  @click:close="removeAttachment(i)"
                >
                  {{ file.name }}
                </v-chip>
              </div>
            </div>
            

          </v-form>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-card-actions>
          <v-btn
            v-if="hasPermission('admin.mails.send-draft')"
            color="primary"
            :loading="sending"
            @click="sendDraft"
          >
            إرسال
          </v-btn>
          <v-btn
            v-if="hasPermission('admin.mails.store-draft')"
            variant="text"
            @click="saveDraft"
          >
            حفظ
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="hasPermission('admin.mails.delete-draft')"
            variant="text"
            color="error"
            @click="deleteDraft"
          >
            حذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiClient from '@/services/api'
import authService from '@/services/authService'
import departmentService from '@/services/departmentService'
import mailPriorityService from '@/services/mailPriorityService'
import { showSuccessAlert, showErrorAlert } from '@/utils/alertUtils'
import { mapGetters } from 'vuex'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'DraftsView',
  data() {
    return {
      selectAll: false,
      selectedDrafts: [],
      search: '',
      editDialog: false,
      currentDraft: null,
      sending: false,
      loading: false,
      currentUserId: null,
      departments: [],
      mailPriorities: [],
      drafts: [],
      textFormatActions: [
        { icon: 'mdi-format-bold', command: 'bold', title: 'غامق' },
        { icon: 'mdi-format-italic', command: 'italic', title: 'مائل' },
        { icon: 'mdi-format-underline', command: 'underline', title: 'تحته خط' },
        { icon: 'mdi-format-strikethrough', command: 'strikeThrough', title: 'يتوسطه خط' }
      ],
      fontSizes: [
        { label: 'صغير جداً', value: '1', preview: '10px' },
        { label: 'صغير', value: '2', preview: '12px' },
        { label: 'عادي', value: '3', preview: '14px' },
        { label: 'كبير', value: '4', preview: '16px' },
        { label: 'كبير جداً', value: '5', preview: '18px' }
      ],
      textColors: [
        { value: '#000000' }, { value: '#434343' }, { value: '#666666' },
        { value: '#999999' }, { value: '#b7b7b7' }, { value: '#cccccc' },
        { value: '#d9d9d9' }, { value: '#efefef' }, { value: '#f3f3f3' },
        { value: '#ffffff' }, { value: '#980000' }, { value: '#ff0000' },
        { value: '#ff9900' }, { value: '#ffff00' }, { value: '#00ff00' },
        { value: '#00ffff' }, { value: '#4a86e8' }, { value: '#0000ff' },
        { value: '#9900ff' }, { value: '#ff00ff' }
      ],
      alignments: [
        { icon: 'mdi-format-align-right', value: 'justifyRight', title: 'محاذاة لليمين' },
        { icon: 'mdi-format-align-center', value: 'justifyCenter', title: 'توسيط' },
        { icon: 'mdi-format-align-left', value: 'justifyLeft', title: 'محاذاة لليسار' }
      ]
    }
  },
  
  async mounted() {
    await this.getCurrentUser()
    await this.loadDepartments()
    await this.loadMailPriorities()
    await this.loadDrafts()
  },
  
  computed: {
    filteredDrafts() {
      if (!this.search) return this.drafts;
      
      const searchLower = this.search.toLowerCase();
      return this.drafts.filter(draft => 
        (draft.subject?.toLowerCase().includes(searchLower)) ||
        (draft.content?.toLowerCase().includes(searchLower))
      );
    },
    hasSelected() {
      return this.selectedDrafts.length > 0;
    },
    ...mapGetters(['sendPermissions']),
    
    // Filter departments based on current department's send permissions
    availableDepartments() {
      if (!this.departments || this.departments.length === 0) {
        return [];
      }
      
      // If no send permissions defined, allow sending to all active departments
      if (!this.sendPermissions || this.sendPermissions.length === 0) {
        return this.departments.filter(dept => dept.active !== false);
      }
      
      // Filter departments based on send permissions
      return this.departments.filter(dept => {
        const hasPermission = this.sendPermissions.includes(dept.id);
        const isActive = dept.active !== false;
        return hasPermission && isActive;
      });
    }
  },
  
  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    async getCurrentUser() {
      try {
        const response = await authService.getProfile()
        this.currentUserId = response.data?.id || response.id
      } catch (error) {
        console.error('Error getting current user:', error)
        showErrorAlert(this.$store, error, 'فشل في تحميل بيانات المستخدم');
      }
    },

    async loadDepartments() {
      try {
        const response = await departmentService.getDepartments()
        this.departments = response.data?.data || response.data || []
      } catch (error) {
        console.error('Error loading departments:', error)
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في تحميل الأقسام'
        })
      }
    },

    async loadMailPriorities() {
      try {
        const response = await mailPriorityService.getMailPriorities()
        this.mailPriorities = response.data || []
      } catch (error) {
        console.error('Error loading mail priorities:', error)
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في تحميل أولويات البريد'
        })
      }
    },

    async loadDrafts() {
      if (!this.currentUserId) {
        console.warn('Cannot load drafts: user ID not available')
        return
      }

      try {
        this.loading = true
        const params = {
      
        }

        const response = await apiClient.get('/admin/mails?type=drafts&_with_[senderDepartment]=&_with_[departments]=&_no_pagination_=1', { params })
        const draftsData = response.data?.data || response.data || []
        
        // Map API response to the format expected by the component
        this.drafts = draftsData.map(draft => ({
          id: draft.id,
          to: (draft.departments || []).filter(d => d.pivot?.recipient_type?.toUpperCase() === 'TO').map(d => d.id),
          toDepartmentNames: (draft.departments || []).filter(d => d.pivot?.recipient_type?.toUpperCase() === 'TO').map(d => d.name),
          cc: (draft.departments || []).filter(d => d.pivot?.recipient_type?.toUpperCase() === 'CC').map(d => d.id),
          ccDepartmentNames: (draft.departments || []).filter(d => d.pivot?.recipient_type?.toUpperCase() === 'CC').map(d => d.name),
          subject: draft.subject || '',
          content: draft.body || '',
          lastModified: new Date(draft.created_at || draft.updated_at),
          attachments: draft.attachments || [],
          mail_priority_id: draft.mail_priority_id,
          urgent: draft.priority?.level === 1
        }))
      } catch (error) {
        console.error('Error loading drafts:', error)
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'فشل في تحميل المسودات: ' + (error.message || 'خطأ غير معروف')
        })
      } finally {
        this.loading = false
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedDrafts = this.filteredDrafts.map(d => d.id);
      } else {
        this.selectedDrafts = [];
      }
    },

    formatDate(date) {
      const now = new Date();
      const draftDate = new Date(date);
      
      if (draftDate.toDateString() === now.toDateString()) {
        return draftDate.toLocaleTimeString('ar-LY', { hour: '2-digit', minute: '2-digit' });
      }
      
      if (draftDate.getFullYear() === now.getFullYear()) {
        return draftDate.toLocaleDateString('ar-LY', { day: 'numeric', month: 'short' });
      }
      
      return draftDate.toLocaleDateString('ar-LY', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    editDraft(draft) {
      this.currentDraft = { ...draft };
      this.editDialog = true;
    },

    async sendDraft() {
      this.sending = true;
      try {
        // Prepare form data for sending (remove draft=1 to send)
        const formData = new FormData();


        
        formData.append('subject', this.currentDraft.subject || '');
        formData.append('body', this.currentDraft.content || '');
        
        if (this.currentDraft.mail_priority_id) {
          formData.append('mail_priority_id', this.currentDraft.mail_priority_id);
        }
        
        // Add to_departments array
        if (this.currentDraft.to && this.currentDraft.to.length > 0) {
          this.currentDraft.to.forEach((deptId, index) => {
            formData.append(`to_departments[${index}]`, deptId);
          });
        }
        
        // Add cc_departments array if exists
        if (this.currentDraft.cc && this.currentDraft.cc.length > 0) {
          this.currentDraft.cc.forEach((deptId, index) => {
            formData.append(`cc_departments[${index}]`, deptId);
          });
        }
        
        // Add attachments if exists
        if (this.currentDraft.attachments && this.currentDraft.attachments.length > 0) {
          this.currentDraft.attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }
        
        // Send the mail (update existing draft)
        const response = await apiClient.post(`/admin/mails/${this.currentDraft.id}/send-draft`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Remove draft from list after sending
        this.drafts = this.drafts.filter(d => d.id !== this.currentDraft.id);
        this.editDialog = false;
        
        showSuccessAlert(this.$store, response, 'تم إرسال المراسلة بنجاح');
      } catch (error) {
        console.error('Error sending draft:', error);
        showErrorAlert(this.$store, error, 'حدث خطأ أثناء إرسال المراسلة');
      } finally {
        this.sending = false;
      }
    },
    
    async saveDraft() {
      try {
        // Prepare form data for saving
        const formData = new FormData();
        
        this.currentDraft.content = this.$refs.editor.innerHTML;
        formData.append('subject', this.currentDraft.subject || '');
        formData.append('body', this.currentDraft.content || '');
      // Keep as draft
        
        if (this.currentDraft.mail_priority_id) {
          formData.append('mail_priority_id', this.currentDraft.mail_priority_id);
        }
        
        // Add to_departments array
        if (this.currentDraft.to && this.currentDraft.to.length > 0) {
          this.currentDraft.to.forEach((deptId, index) => {
            formData.append(`to_departments[${index}]`, deptId);
          });
        }
        
        // Add cc_departments array if exists
        if (this.currentDraft.cc && this.currentDraft.cc.length > 0) {
          this.currentDraft.cc.forEach((deptId, index) => {
            formData.append(`cc_departments[${index}]`, deptId);
          });
        }
        
        // Add attachments if exists
        if (this.currentDraft.attachments && this.currentDraft.attachments.length > 0) {
          this.currentDraft.attachments.forEach((file, index) => {
            formData.append(`attachments[${index}]`, file);
          });
        }
        
        // Update the existing draft
        await apiClient.post(`/admin/mails/${this.currentDraft.id}/update-draft`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        this.editDialog = false;
        await this.loadDrafts(); // Refresh the drafts list
        
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-content-save',
          timeout: 3000,
          closable: true,
          message: 'تم حفظ المسودة بنجاح'
        });
      } catch (error) {
        console.error('Error saving draft:', error);
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'حدث خطأ أثناء حفظ المسودة: ' + (error.message || 'خطأ غير معروف')
        });
      }
    },
    async deleteDraft() {
      try {
        await apiClient.delete(`/admin/mails/${this.currentDraft.id}`);
        
        this.drafts = this.drafts.filter(d => d.id !== this.currentDraft.id);
        this.editDialog = false;
        
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000,
          closable: true,
          message: 'تم حذف المسودة بنجاح'
        });
      } catch (error) {
        console.error('Error deleting draft:', error);
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'حدث خطأ أثناء حذف المسودة: ' + (error.message || 'خطأ غير معروف')
        });
      }
    },
    
    async deleteSelected() {
      if (this.selectedDrafts.length === 0) return;
      
      try {
        // Delete all selected drafts
        const deletePromises = this.selectedDrafts.map(draftId => 
          apiClient.delete(`/admin/mails/${draftId}`)
        );
        
        await Promise.all(deletePromises);
        
        this.drafts = this.drafts.filter(draft => !this.selectedDrafts.includes(draft.id));
        const deletedCount = this.selectedDrafts.length;
        this.selectedDrafts = [];
        this.selectAll = false;
        
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000,
          closable: true,
          message: `تم حذف ${deletedCount} مسودة بنجاح`
        });
      } catch (error) {
        console.error('Error deleting selected drafts:', error);
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: 'حدث خطأ أثناء حذف المسودات: ' + (error.message || 'خطأ غير معروف')
        });
      }
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      if (!this.currentDraft.attachments) {
        this.currentDraft.attachments = [];
      }
      this.currentDraft.attachments.push(...files);
      this.$refs.fileInput.value = '';
    },
    removeAttachment(index) {
      this.currentDraft.attachments.splice(index, 1);
    },
    formatText(command) {
      document.execCommand(command, false, null);
      this.$refs.editor.focus();
    },
    setFontSize(size) {
      document.execCommand('fontSize', false, size);
      this.$refs.editor.focus();
    },
    setTextColor(color) {
      document.execCommand('foreColor', false, color);
      this.$refs.editor.focus();
    },
    setAlignment(command) {
      document.execCommand(command, false, null);
      this.$refs.editor.focus();
    },
    updateContent(event) {
      this.currentDraft.content = event.target.innerHTML;
    },
    stripHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    }
  }
}
</script>

<style scoped>
.drafts-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drafts-toolbar {
  border-radius: 8px;
  background-color: #f8f9fa;
}

.drafts-list {
  flex-grow: 1;
  overflow-y: auto;
  border-radius: 8px;
}

.search-field {
  width: 240px;
}

.selected-draft {
  background-color: rgba(232, 240, 254, 0.8);
}

/* RTL specific styles */
.v-list-item__prepend {
  margin-left: 8px !important;
  margin-right: 0 !important;
}

.v-input--checkbox .v-input__slot {
  justify-content: flex-end;
}

.formatting-toolbar {
  border: 1px solid #e0e0e0;
  border-bottom: none;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: #f8f9fa;
}

.editor {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  min-height: 200px;
  padding: 12px;
  outline: none;
  font-family: 'Cairo', sans-serif;
}

.active-format {
  background-color: rgba(0, 0, 0, 0.1);
}

:deep(.v-btn-group .v-btn) {
  height: 36px;
  width: 36px;
}

:deep(.v-btn-group) {
  flex-wrap: wrap;
}

.font-size-btn {
  min-width: 36px;
}
</style> 